import * as $dict from "../gleam_stdlib/gleam/dict.mjs";
import * as $option from "../gleam_stdlib/gleam/option.mjs";
import { CustomType as $CustomType } from "./gleam.mjs";

/**
 * Assets that are loaded in `preload`.
 * 
 * @ignore
 */
class Assets extends $CustomType {
  constructor(fonts, images) {
    super();
    this.fonts = fonts;
    this.images = images;
  }
}

class BaseConfig extends $CustomType {
  constructor(init, draw, on_tick, on_key_pressed, on_key_released, on_mouse_moved, on_mouse_clicked) {
    super();
    this.init = init;
    this.draw = draw;
    this.on_tick = on_tick;
    this.on_key_pressed = on_key_pressed;
    this.on_key_released = on_key_released;
    this.on_mouse_moved = on_mouse_moved;
    this.on_mouse_clicked = on_mouse_clicked;
  }
}

class ConfigWithLoadingAssets extends $CustomType {
  constructor(preload, init, draw, on_tick, on_key_pressed, on_key_released, on_mouse_moved, on_mouse_clicked) {
    super();
    this.preload = preload;
    this.init = init;
    this.draw = draw;
    this.on_tick = on_tick;
    this.on_key_pressed = on_key_pressed;
    this.on_key_released = on_key_released;
    this.on_mouse_moved = on_mouse_moved;
    this.on_mouse_clicked = on_mouse_clicked;
  }
}

/**
 * Creates an initial empty asset set.
 */
export function initialize_assets() {
  return new Assets($dict.new$(), $dict.new$());
}

/**
 * Adds a font to the asset set.
 */
export function insert_font(assets, font_key, font) {
  return new Assets($dict.insert(assets.fonts, font_key, font), assets.images);
}

/**
 * Adds an image to the asset set.
 */
export function insert_image(assets, image_key, image) {
  return new Assets(assets.fonts, $dict.insert(assets.images, image_key, image));
}

/**
 * Get a font from the asset set.
 */
export function get_font(assets, font_key) {
  return $dict.get(assets.fonts, font_key);
}

/**
 * Get an image from the asset set.
 */
export function get_image(assets, image_key) {
  return $dict.get(assets.images, image_key);
}

/**
 * Creates a minimal sketch configuration.
 */
export function create_sketch(init, draw) {
  return new BaseConfig(
    init,
    draw,
    new $option.None(),
    new $option.None(),
    new $option.None(),
    new $option.None(),
    new $option.None(),
  );
}

/**
 * Creates a sketch configuration that supports preloading assets.
 */
export function create_sketch_with_preloading(preload, init, draw) {
  return new ConfigWithLoadingAssets(
    preload,
    init,
    draw,
    new $option.None(),
    new $option.None(),
    new $option.None(),
    new $option.None(),
    new $option.None(),
  );
}

/**
 * Sets the `on_tick` function to be called.
 */
export function set_on_tick(s, on_tick) {
  if (s instanceof BaseConfig) {
    let init = s.init;
    let draw = s.draw;
    let on_key_pressed = s.on_key_pressed;
    let on_key_released = s.on_key_released;
    let on_mouse_moved = s.on_mouse_moved;
    let on_mouse_clicked = s.on_mouse_clicked;
    return new BaseConfig(
      init,
      draw,
      new $option.Some(on_tick),
      on_key_pressed,
      on_key_released,
      on_mouse_moved,
      on_mouse_clicked,
    );
  } else {
    let preload = s.preload;
    let init = s.init;
    let draw = s.draw;
    let on_key_pressed = s.on_key_pressed;
    let on_key_released = s.on_key_released;
    let on_mouse_moved = s.on_mouse_moved;
    let on_mouse_clicked = s.on_mouse_clicked;
    return new ConfigWithLoadingAssets(
      preload,
      init,
      draw,
      new $option.Some(on_tick),
      on_key_pressed,
      on_key_released,
      on_mouse_moved,
      on_mouse_clicked,
    );
  }
}

/**
 * Sets the `on_key_pressed` function to be called.
 */
export function set_on_key_pressed(s, on_key_pressed) {
  if (s instanceof BaseConfig) {
    let init = s.init;
    let draw = s.draw;
    let on_tick = s.on_tick;
    let on_key_released = s.on_key_released;
    let on_mouse_moved = s.on_mouse_moved;
    let on_mouse_clicked = s.on_mouse_clicked;
    return new BaseConfig(
      init,
      draw,
      on_tick,
      new $option.Some(on_key_pressed),
      on_key_released,
      on_mouse_moved,
      on_mouse_clicked,
    );
  } else {
    let preload = s.preload;
    let init = s.init;
    let draw = s.draw;
    let on_tick = s.on_tick;
    let on_key_released = s.on_key_released;
    let on_mouse_moved = s.on_mouse_moved;
    let on_mouse_clicked = s.on_mouse_clicked;
    return new ConfigWithLoadingAssets(
      preload,
      init,
      draw,
      on_tick,
      new $option.Some(on_key_pressed),
      on_key_released,
      on_mouse_moved,
      on_mouse_clicked,
    );
  }
}

/**
 * Sets the `on_key_released` function to be called.
 */
export function set_on_key_released(s, on_key_released) {
  if (s instanceof BaseConfig) {
    let init = s.init;
    let draw = s.draw;
    let on_tick = s.on_tick;
    let on_key_pressed = s.on_key_pressed;
    let on_mouse_moved = s.on_mouse_moved;
    let on_mouse_clicked = s.on_mouse_clicked;
    return new BaseConfig(
      init,
      draw,
      on_tick,
      on_key_pressed,
      new $option.Some(on_key_released),
      on_mouse_moved,
      on_mouse_clicked,
    );
  } else {
    let preload = s.preload;
    let init = s.init;
    let draw = s.draw;
    let on_tick = s.on_tick;
    let on_key_pressed = s.on_key_pressed;
    let on_mouse_moved = s.on_mouse_moved;
    let on_mouse_clicked = s.on_mouse_clicked;
    return new ConfigWithLoadingAssets(
      preload,
      init,
      draw,
      on_tick,
      on_key_pressed,
      new $option.Some(on_key_released),
      on_mouse_moved,
      on_mouse_clicked,
    );
  }
}

/**
 * Sets the `on_mouse_moved` function to be called.
 */
export function set_on_mouse_moved(s, on_mouse_moved) {
  if (s instanceof BaseConfig) {
    let init = s.init;
    let draw = s.draw;
    let on_tick = s.on_tick;
    let on_key_pressed = s.on_key_pressed;
    let on_key_released = s.on_key_released;
    let on_mouse_clicked = s.on_mouse_clicked;
    return new BaseConfig(
      init,
      draw,
      on_tick,
      on_key_pressed,
      on_key_released,
      new $option.Some(on_mouse_moved),
      on_mouse_clicked,
    );
  } else {
    let preload = s.preload;
    let init = s.init;
    let draw = s.draw;
    let on_tick = s.on_tick;
    let on_key_pressed = s.on_key_pressed;
    let on_key_released = s.on_key_released;
    let on_mouse_clicked = s.on_mouse_clicked;
    return new ConfigWithLoadingAssets(
      preload,
      init,
      draw,
      on_tick,
      on_key_pressed,
      on_key_released,
      new $option.Some(on_mouse_moved),
      on_mouse_clicked,
    );
  }
}

/**
 * Sets the `on_mouse_clicked` function to be called.
 */
export function set_on_mouse_clicked(s, on_mouse_clicked) {
  if (s instanceof BaseConfig) {
    let init = s.init;
    let draw = s.draw;
    let on_tick = s.on_tick;
    let on_key_pressed = s.on_key_pressed;
    let on_key_released = s.on_key_released;
    let on_mouse_moved = s.on_mouse_moved;
    return new BaseConfig(
      init,
      draw,
      on_tick,
      on_key_pressed,
      on_key_released,
      on_mouse_moved,
      new $option.Some(on_mouse_clicked),
    );
  } else {
    let preload = s.preload;
    let init = s.init;
    let draw = s.draw;
    let on_tick = s.on_tick;
    let on_key_pressed = s.on_key_pressed;
    let on_key_released = s.on_key_released;
    let on_mouse_moved = s.on_mouse_moved;
    return new ConfigWithLoadingAssets(
      preload,
      init,
      draw,
      on_tick,
      on_key_pressed,
      on_key_released,
      on_mouse_moved,
      new $option.Some(on_mouse_clicked),
    );
  }
}
